import { useState } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Printer, Copy, Check, Shield } from "lucide-react";
import { Button } from "../../ui/button";
import { useSelector } from "react-redux";
import {
  getCsatSubmission,
  getFeedbackForm,
  getProfileForm,
  getWhiteLabelLogos,
} from "../../../redux/reducer/incbotFeedbackReducer";
import logo from "../../../assets/images/incruiter-main-logo.svg";
import {
  formatDate,
  formatDuration,
  getAccessToken,
  resolveFeedbackStatusChip,
} from "../../../utils/helper";
import { SmilePlus } from "lucide-react";
import { Tooltip } from "react-tooltip";

const ProfileHeader = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { interviewId = "", roomId = "" } = useParams();
  const [searchParams] = useSearchParams();
  const token = getAccessToken;

  const routeType = location.pathname.split("/").filter(Boolean)[0] ?? "";
  const profileForm = useSelector(getProfileForm);
  const feedbackForm = useSelector(getFeedbackForm);
  const whiteLabelLogos = useSelector(getWhiteLabelLogos);
  const csatSubmission = useSelector(getCsatSubmission);
  const [copied, setCopied] = useState(false);
  const statusChip = resolveFeedbackStatusChip(profileForm?.status, profileForm?.feedbackStatus);
  const resumeMatchScore = feedbackForm?.overallFeedback?.[1]?.value;
  const resumeMatchText = typeof resumeMatchScore === "number" ? `${resumeMatchScore * 10}%` : "";

  const handleRateInterview = () => {
    if (csatSubmission) return;
    const expires = searchParams.get("expires") || "";
    const signature = searchParams.get("signature") || "";
    const params = new URLSearchParams();
    if (expires) params.set("expires", expires);
    if (signature) params.set("signature", signature);
    const query = params.toString();
    navigate(`/csat/${interviewId}/${roomId}${query ? `?${query}` : ""}`, {
      state: routeType,
    });
  };

  const handleCopyLink = () => {
    navigator.clipboard
      .writeText(window.location.href)
      .then(() => {
        setCopied(true);
        setTimeout(() => {
          setCopied(false);
        }, 1000);
      })
      .catch((err) => console.error("Failed to copy link:", err));
  };
  return (
    <div className="bg-white border-none border border-gray-200 p-3 md:p-6">
      <div className="flex flex-col gap-2 w-full align-center justify-center">
        {/* Mobile view */}
        <div className="sm:hidden flex-shrink-0 w-full flex justify-center mb-2">
          <img
            src={whiteLabelLogos?.logo ? whiteLabelLogos?.logo : logo}
            alt="InCruiter"
            className="h-10 w-20 object-contain md:h-12"
          />
        </div>
        <div className="flex gap-2 sm:hidden">
          <div className="flex flex-col gap-2 items-center justify-center">
            <div className="size-24 md:size-32 flex-shrink-0 bg-gradient-to-br from-teal-500 to-blue-600 rounded-2xl flex items-center justify-center text-white text-xl font-bold overflow-hidden">
              {profileForm?.candidateImage ? (
                <img src={profileForm?.candidateImage} className="w-full h-full object-cover" />
              ) : (
                <>
                  {profileForm?.name
                    .split(" ")
                    .map((n: any) => n[0])
                    .join("")}
                </>
              )}
            </div>
          </div>

          {/* Candidate info */}
          <div className="flex-1 overflow-hidden">
            <div className="group relative">
              <h1 className="text-lg md:text-2xl font-bold text-gray-900 truncate">
                {profileForm?.name || ""}
              </h1>
            </div>
            <div className="flex items-center gap-2 flex-wrap mt-1 mb-1.5">
              <div className="max-w-full truncate border border-[#E2E8F0] px-2 py-[2px] rounded-3xl bg-white">
                <span className="text-[11px] text-gray-600 truncate block">
                  {profileForm?.code || ""}
                </span>
              </div>
              <div className="max-w-full truncate px-2.5 py-[2px] rounded-3xl bg-[#12B6BC]">
                <span className="text-[10px] leading-4 text-white whitespace-nowrap block">
                  {resumeMatchText} Resume Match
                </span>
              </div>
            </div>
            <div className="group relative">
              <p className="text-gray-700 text-[9px] md:text-md font-medium ">
                {profileForm?.position || ""} • {profileForm?.company || ""}
              </p>
            </div>
            <div className="group relative">
              <p className="text-gray-500 text-[9px] md:text-sm">
                {profileForm?.email || ""} • {profileForm?.phone?.replace(/\+\+/g, "+") || ""}
              </p>
            </div>
            <div className="flex items-center gap-2 max-sm:mb-2">
              <div className="flex items-center space-x-2 text-gray-600">
                <span className="text-sm max-sm:text-[9px]">
                  {formatDate(profileForm?.interviewDate, 0)}
                </span>
              </div>
              <div className="flex items-center space-x-2 text-gray-600">
                <span className="text-sm max-sm:text-[9px]">
                  {formatDuration(feedbackForm?.totalDuration || 0)}
                </span>
              </div>
              {profileForm?.interviewLanguage && (
                <div className="flex items-center space-x-2 text-gray-600">
                  <span className="text-sm max-sm:text-[9px]">
                    Interview Language: {profileForm.interviewLanguage}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Desktop view */}
        <div className="flex justify-between items-center max-sm:hidden">
          <div className="size-24 md:size-32 flex-shrink-0 bg-gradient-to-br from-teal-500 to-blue-600 rounded-2xl flex items-center justify-center text-white text-2xl font-bold overflow-hidden">
            {profileForm?.candidateImage ? (
              <img src={profileForm?.candidateImage} className="w-full h-full object-cover" />
            ) : (
              <>
                {profileForm?.name
                  .split(" ")
                  .map((n: any) => n[0])
                  .join("")}
              </>
            )}
          </div>
          <div className="min-w-0 sm:block max-sm:hidden flex-1 ml-4 overflow-hidden">
            <div className="flex items-center space-x-2 flex-wrap">
              {/* name */}
              <div className="truncate group relative">
                <h1 className="text-xl md:text-2xl font-bold text-gray-900 truncate">
                  {profileForm?.name || ""}
                </h1>
                <span className="invisible group-hover:visible absolute left-0 top-full z-10 bg-white p-1 shadow-md rounded text-sm">
                  {profileForm?.name || ""}
                </span>
              </div>
            </div>
            {/* code and resume score section*/}
            <div className="flex items-center space-x-2">
              {/* code */}
              <div className="truncate group relative border border-[#E2E8F0] px-2 py-[2px] rounded-3xl">
                <span className="text-md md:text-sm text-gray-500 truncate">
                  {profileForm?.code || ""}
                </span>
                <span className="invisible group-hover:visible absolute left-0 top-full z-10 bg-white p-1 shadow-md rounded text-sm">
                  {profileForm?.code || ""}
                </span>
              </div>
              {/* resume score */}
              <div className="truncate group relative px-2 py-[2px] rounded-3xl bg-[#12B6BC]">
                <span className="text-md md:text-sm text-[#FFFFFF] truncate">
                  {resumeMatchText} Resume Match
                </span>
                <span className="invisible group-hover:visible absolute left-0 top-full z-10 bg-white p-1 shadow-md rounded text-sm">
                  {resumeMatchText}{" "}
                </span>
              </div>
            </div>
            <div className="truncate group relative">
              <p className="text-gray-700 text-sm md:text-md font-medium truncate">
                {profileForm?.position || ""} • {profileForm?.company || ""}
              </p>
              <span className="invisible group-hover:visible absolute left-0 top-full z-10 bg-white p-1 shadow-md rounded text-sm">
                {profileForm?.position || ""} • {profileForm?.company || ""}
              </span>
            </div>
            <div className="truncate group relative">
              <p className="text-gray-500 text-xs md:text-sm truncate">
                {profileForm?.email || ""} • {profileForm?.phone?.replace(/\+\+/g, "+") || ""}
              </p>
              <span className="invisible group-hover:visible absolute left-0 top-full z-10 bg-white p-1 shadow-md rounded text-sm">
                {profileForm?.email || ""} • {profileForm?.phone?.replace(/\+\+/g, "+") || ""}
              </span>
            </div>
            <div className="flex gap-3 flex-wrap">
              <div className="flex items-center space-x-2 text-gray-600">
                <span className="text-sm max-sm:text-xs">
                  {formatDate(profileForm?.interviewDate, 0)}
                </span>
              </div>
              <div className="flex items-center space-x-2 text-gray-600">
                <span className="text-sm max-sm:text-xs">
                  {formatDuration(feedbackForm?.totalDuration || 0)}
                </span>
              </div>
              {profileForm?.interviewLanguage && (
                <div className="flex items-center space-x-2 text-gray-600">
                  <span className="text-sm max-sm:text-xs">
                    Interview Language: {profileForm.interviewLanguage}
                  </span>
                </div>
              )}
            </div>
          </div>
          <div className="max-sm:hidden flex-shrink-0">
            <img
              src={whiteLabelLogos?.logo ? whiteLabelLogos?.logo : logo}
              alt="InCruiter"
              className="h-14 w-32 object-contain"
            />
          </div>
        </div>
      </div>

      {/* Bottom action bar */}
      <div className="flex items-center justify-between mt-2 md:mt-6 max-sm:flex-col">
        <div className="flex items-center space-x-3 max-sm:w-full max-sm:mb-2 max-sm:flex-wrap max-sm:gap-2 max-sm:space-x-0">
          {profileForm?.status && (
            <div
              className={`flex items-center space-x-2 px-3 py-1 rounded-full max-sm:px-2 max-sm:py-0.5 max-sm:space-x-1.5
                ${
                  profileForm.status === "Recommended"
                    ? "bg-green-50 text-green-700"
                    : profileForm.status === "Cautiously Recommended"
                      ? "bg-yellow-50 text-yellow-700"
                      : "bg-red-50 text-red-700"
                }
              `}
              style={statusChip.style}
            >
              <span className="text-[10px] md:text-sm font-medium whitespace-normal md:whitespace-nowrap">
                {statusChip.label}
              </span>
            </div>
          )}

          {profileForm?.deepfakeDetected !== undefined && !profileForm?.deepfakeDetected ? (
            <div
              className={`flex items-center space-x-2 px-3 py-1 rounded-full max-sm:px-2 max-sm:py-0.5 max-sm:space-x-1.5 ${
                profileForm.deepfakeDetected
                  ? "bg-red-50 text-red-700"
                  : "bg-green-50 text-green-700"
              }`}
            >
              <Shield className="w-4 h-4 max-sm:w-3 max-sm:h-3" />
              <span className="text-[10px] md:text-sm font-medium whitespace-normal md:whitespace-nowrap">
                {profileForm.deepfakeDetected ? "Deepfake Detected" : "Deepfake Not Detected"}
              </span>
            </div>
          ) : (
            <>
              {profileForm?.deepFakeAudioDetection && (
                <div className="flex items-center space-x-2 px-3 py-1 rounded-full bg-red-50 text-red-700 max-sm:px-2 max-sm:py-0.5 max-sm:space-x-1.5">
                  <Shield className="w-4 h-4 max-sm:w-3 max-sm:h-3" />
                  <span className="text-[10px] md:text-sm font-medium whitespace-normal md:whitespace-nowrap">
                    Deepfake Audio Detected
                  </span>
                </div>
              )}

              {profileForm?.deepFakeVideoDetection && (
                <div className="flex items-center space-x-2 px-3 py-1 rounded-full bg-red-50 text-red-700 max-sm:px-2 max-sm:py-0.5 max-sm:space-x-1.5">
                  <Shield className="w-4 h-4 max-sm:w-3 max-sm:h-3" />
                  <span className="text-[10px] md:text-sm font-medium whitespace-normal md:whitespace-nowrap">
                    Deepfake Video Detected
                  </span>
                </div>
              )}
            </>
          )}
        </div>

        <div className="flex items-center space-x-2 max-sm:w-full">
          <Button
            onClick={() => window.print()}
            variant="outline"
            size="sm"
            className="pr-11 print:hidden max-sm:w-1/2"
          >
            <Printer className="w-4 h-4" />
            Print
          </Button>
          <Button
            onClick={handleCopyLink}
            variant="outline"
            size="sm"
            className="print:hidden max-sm:w-1/2"
          >
            {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
            {copied ? "Link copied" : "Copy Link"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ProfileHeader;
